import { useState, useEffect, useRef, useTransition } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useEmpresa } from "../contexts/EmpresaContext";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  Truck,
  ShoppingBag,
  Settings,
  LogOut,
  Menu,
  X,
  Building2,
  ChevronDown,
  Home,
  Shield,
  ScrollText,
  Bell,
  MessageCircle,
  Bot,
  Clock,
  Container,
  BookUser,
  TrendingUp,
  Wallet,
  Landmark,
  MoreHorizontal,
  User,
  ChevronRight,
  Warehouse,
  Briefcase,
  Ship,
  Zap,
  Car,
  Gavel,
  DollarSign,
  FileText,
  Share2,
  BarChart3,
  CreditCard,
  Receipt,
  Calculator,
  Search,
  Megaphone,
  Database,
  BookOpen,
  Download,
  Upload
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  // Todos los roles pueden ver
  { path: "/dashboard", label: "Dashboard", shortLabel: "Inicio", icon: LayoutDashboard, mobileOrder: 1 },
  { path: "/asistencia", label: "Asistencia", shortLabel: "Asistir", icon: Clock, mobileOrder: 2 },
  
  // Panel de Tiempo Real (solo gerente y superadmin)
  { path: "/tiempo-real", label: "Tiempo Real", shortLabel: "En Vivo", icon: Zap, roles: ["superadmin", "gerente"], highlight: true },
  
  // Módulo unificado de Importaciones/Contenedores
  { path: "/importaciones-eco", label: "Importaciones", shortLabel: "Import.", icon: Package, roles: ["superadmin", "admin", "gerente"] },
  
  // GALPONES/INVENTARIO - UN SOLO MÓDULO UNIFICADO
  { path: "/galpones", label: "Galpones/Inventario", shortLabel: "Stock", icon: Warehouse, roles: ["superadmin", "admin", "gerente", "logistica", "operador"] },
  
  // Módulos principales de negocio
  { path: "/ventas-historico", label: "Histórico Ventas", shortLabel: "Historial", icon: TrendingUp, roles: ["superadmin", "admin", "gerente"] },
  { path: "/banco-eco", label: "Banco ECO", shortLabel: "Banco", icon: Landmark, roles: ["superadmin", "admin", "gerente"] },
  { path: "/deuda-edmundo", label: "Deuda Edmundo", shortLabel: "Deuda", icon: FileText, roles: ["superadmin", "admin", "gerente"] },
  
  // Operaciones - según rol
  { path: "/ventas", label: "Ventas", shortLabel: "Ventas", icon: ShoppingCart, roles: ["superadmin", "admin", "gerente", "vendedor"], mobileOrder: 3 },
  { path: "/clientes", label: "Clientes", shortLabel: "CRM", icon: Users, roles: ["superadmin", "admin", "gerente", "vendedor"], mobileOrder: 5 },
  { path: "/compras", label: "Compras", shortLabel: "Compras", icon: ShoppingBag, roles: ["superadmin", "admin", "gerente"] },
  { path: "/logistica", label: "Entregas", shortLabel: "Entregas", icon: Truck, roles: ["superadmin", "admin", "gerente", "logistica"] },
  
  // RRHH Completo - superadmin y gerente (información sensible de salarios)
  { path: "/rrhh-completo", label: "Recursos Humanos", shortLabel: "RRHH", icon: Briefcase, roles: ["superadmin", "gerente", "admin"] },
  
  // Reporte Diario y Finanzas
  { path: "/reporte-diario-eco", label: "Reporte Diario", shortLabel: "Reporte", icon: BarChart3, roles: ["superadmin", "admin", "gerente"] },
  { path: "/finanzas-eco", label: "Pagos a Inversionista", shortLabel: "Pagos", icon: DollarSign, roles: ["superadmin", "admin", "gerente"] },
  
  // WhatsApp - gerencia y arriba
  { path: "/whatsapp", label: "WhatsApp Alertas", shortLabel: "Alertas", icon: MessageCircle, roles: ["superadmin", "admin"] },
  { path: "/bot-whatsapp", label: "Bot Clientes", shortLabel: "Bot", icon: Bot, roles: ["superadmin", "admin", "gerente", "logistica"] },
  
  // Administración - solo admin/superadmin
  { path: "/usuarios", label: "Usuarios", shortLabel: "Users", icon: Settings, roles: ["superadmin", "admin"] },
  
  // Solo SuperAdmin
  { path: "/solicitudes", label: "Control Cambios", shortLabel: "Control", icon: Shield, roles: ["superadmin", "gerente"], showBadge: true },
  { path: "/auditoria", label: "Auditoría", shortLabel: "Audit", icon: ScrollText, roles: ["superadmin", "gerente"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { empresas, empresaActual, cambiarEmpresa } = useEmpresa();
  const location = useLocation();
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [solicitudesPendientes, setSolicitudesPendientes] = useState(0);

  const fetchSolicitudesPendientes = async () => {
    try {
      const res = await axios.get(`${API}/solicitudes-cambio/pendientes/count`);
      setSolicitudesPendientes(res.data.count || 0);
    } catch (error) {
      console.error("Error fetching solicitudes:", error);
    }
  };

  // Fetch solicitudes pendientes cada 30 segundos para superadmin/gerente (roles que pueden aprobar)
  useEffect(() => {
    if (!["superadmin", "gerente"].includes(user?.role)) return;
    
    // Llamada inicial
    const loadInitial = async () => {
      await fetchSolicitudesPendientes();
    };
    loadInitial();
    
    // Intervalo para actualizaciones
    const interval = setInterval(fetchSolicitudesPendientes, 30000);
    return () => clearInterval(interval);
  }, [user?.role]);

  const filteredNavItems = navItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(user?.role);
  });

  // Items para la barra inferior móvil (los 4 principales + "Más")
  const mobileNavItems = filteredNavItems
    .filter(item => item.mobileOrder)
    .sort((a, b) => (a.mobileOrder || 99) - (b.mobileOrder || 99))
    .slice(0, 4);
  
  // Items restantes para el menú "Más"
  const moreNavItems = filteredNavItems.filter(
    item => !mobileNavItems.find(m => (m.path || m.external) === (item.path || item.external))
  );

  const renderNavItem = (item, mobile = false, compact = false) => {
    const isActive = item.path && location.pathname === item.path;
    const Icon = item.icon;
    const showBadge = item.showBadge && solicitudesPendientes > 0;
    
    // Si es link externo, usar <a> en lugar de <Link>
    if (item.external) {
      return (
        <a
          key={item.external}
          href={item.external}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            if (mobile) setSidebarOpen(false);
            setMoreMenuOpen(false);
          }}
          className={`sidebar-item relative ${compact ? "py-2" : ""}`}
          data-testid={`nav-external-${item.labelKey}`}
        >
          <div className="relative">
            <Icon size={compact ? 20 : 22} strokeWidth={2} />
          </div>
          <span className={compact ? "text-sm" : "text-base"}>{item.label}</span>
          {compact && <ChevronRight size={16} className="ml-auto text-slate-400" />}
        </a>
      );
    }
    
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={(e) => {
          if (location.pathname !== item.path) {
            e.preventDefault();
            startTransition(() => navigate(item.path));
          }
          if (mobile) setSidebarOpen(false);
          setMoreMenuOpen(false);
        }}
        className={`sidebar-item ${isActive ? "sidebar-item-active" : ""} relative ${compact ? "py-2" : ""}`}
        data-testid={`nav-${(item.path || "").replace("/", "") || "dashboard"}`}
      >
        <div className="relative">
          <Icon size={compact ? 20 : 22} strokeWidth={2} />
          {showBadge && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
              {solicitudesPendientes > 9 ? "9+" : solicitudesPendientes}
            </span>
          )}
        </div>
        <span className={compact ? "text-sm" : "text-base"}>{item.label}</span>
        {compact && <ChevronRight size={16} className="ml-auto text-slate-400" />}
      </Link>
    );
  };

  const handleGoHome = () => {
    navigate("/");
    setSidebarOpen(false);
    setMoreMenuOpen(false);
  };

  const [exportando, setExportando] = useState(false);
  const [restaurando, setRestaurando] = useState(false);
  const restoreInputRef = useRef(null);
  
  const handleDumpDB = async () => {
    if (exportando) return;
    setExportando(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/backups/dump`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error al crear dump");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mongodb_dump_${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Dump error:", err);
      alert("Error al crear dump: " + err.message);
    } finally {
      setExportando(false);
    }
  };

  const handleRestoreDB = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.zip')) {
      alert('Solo se aceptan archivos .zip');
      if (restoreInputRef.current) restoreInputRef.current.value = '';
      return;
    }
    const dropExisting = window.confirm(
      '¿Deseas BORRAR las colecciones existentes antes de restaurar?\n\n' +
      'SÍ = Reemplaza completamente la base de datos\n' +
      'NO = Agrega/actualiza sin borrar'
    );
    if (!window.confirm('¿Confirmas restaurar este dump? Esta acción puede modificar datos.')) {
      if (restoreInputRef.current) restoreInputRef.current.value = '';
      return;
    }
    setRestaurando(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/backups/restore?drop_existing=${dropExisting}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error restaurando dump');
      alert('Dump restaurado exitosamente');
      window.location.reload();
    } catch (err) {
      alert("Error al restaurar: " + err.message);
    } finally {
      setRestaurando(false);
      if (restoreInputRef.current) restoreInputRef.current.value = '';
    }
  };

  const filteredEmpresas = empresas;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation transition progress bar */}
      {isPending && <div className="nav-progress-bar" />}
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 h-screen fixed left-0 top-0 z-40">
        {/* Logo de Empresa o Sistema */}
        <div className="p-6 border-b border-slate-200">
          {empresaActual?.logo_url ? (
            <button onClick={handleGoHome} className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="go-home">
              <div 
                className="w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center bg-white border shadow-sm"
                style={{ borderColor: empresaActual?.color_principal || '#e2e8f0' }}
              >
                <img 
                  src={empresaActual.logo_url} 
                  alt={empresaActual.nombre}
                  className="w-full h-full object-contain p-1"
                />
              </div>
              <div className="text-left">
                <h1 
                  className="text-lg font-bold leading-tight"
                  style={{ color: empresaActual?.color_principal || '#1e3a8a' }}
                >
                  {empresaActual.nombre}
                </h1>
                <p className="text-xs text-slate-500">ERP + CRM</p>
              </div>
            </button>
          ) : (
            <button onClick={handleGoHome} className="text-left hover:opacity-80 transition-opacity" data-testid="go-home">
              <h1 className="text-2xl font-bold text-blue-900">EDMUNDO</h1>
              <p className="text-sm text-slate-500 mt-1">ERP + CRM</p>
            </button>
          )}
        </div>

        {/* Volver a Inicio */}
        <div className="p-4 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <button
                onClick={handleGoHome}
                className="flex-1 flex items-center gap-3 px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-900 rounded-lg font-medium transition-colors"
                style={{ 
                  backgroundColor: empresaActual?.color_principal ? `${empresaActual.color_principal}15` : undefined,
                  color: empresaActual?.color_principal || undefined
                }}
                data-testid="back-to-home"
              >
                <Home size={20} />
                <span>Volver a Inicio</span>
              </button>
              {user?.role === "superadmin" && (
                <>
                  <button
                    onClick={handleDumpDB}
                    disabled={exportando}
                    className="flex items-center justify-center p-3 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-600 hover:text-slate-900 transition-colors disabled:opacity-50"
                    title="Descargar Dump MongoDB (.zip)"
                    data-testid="dump-db-btn"
                  >
                    <Download size={20} className={exportando ? "animate-bounce" : ""} />
                  </button>
                  <input
                    type="file"
                    ref={restoreInputRef}
                    onChange={handleRestoreDB}
                    accept=".zip"
                    className="hidden"
                  />
                  <button
                    onClick={() => restoreInputRef.current?.click()}
                    disabled={restaurando}
                    className="flex items-center justify-center p-3 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-600 hover:text-slate-900 transition-colors disabled:opacity-50"
                    title="Restaurar Dump MongoDB (.zip)"
                    data-testid="restore-db-btn"
                  >
                    <Upload size={20} className={restaurando ? "animate-spin" : ""} />
                  </button>
                </>
              )}
            </div>
          </div>

        {/* Empresa Selector eliminado - ya no se usa */}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto scrollbar-thin">
          {filteredNavItems.map((item) => renderNavItem(item))}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-900 font-bold text-lg">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-800 truncate">{user?.name}</p>
              <p className="text-sm text-slate-500 capitalize">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 p-3 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={20} />
            <span className="font-medium">Cerrar Sesión</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header - Mejorado */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-3 safe-area-top">
        {/* Logo/Empresa */}
        <button onClick={handleGoHome} className="flex items-center gap-2 min-w-0 flex-1">
          {empresaActual?.logo_url ? (
            <>
              <div className="w-8 h-8 rounded-lg overflow-hidden bg-white border border-slate-200 flex-shrink-0">
                <img 
                  src={empresaActual.logo_url} 
                  alt={empresaActual.nombre}
                  className="w-full h-full object-contain"
                />
              </div>
              <span 
                className="text-base font-bold truncate"
                style={{ color: empresaActual?.color_principal || '#1e3a8a' }}
              >
                {empresaActual.nombre}
              </span>
            </>
          ) : (
            <span className="text-lg font-bold text-blue-900">EDMUNDO</span>
          )}
        </button>
        
        {/* Acciones rápidas */}
        <div className="flex items-center gap-1">
          {/* Botón Home */}
          <button
            onClick={handleGoHome}
            className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
            data-testid="mobile-home-btn"
          >
            <Home size={22} />
          </button>
          
          {/* Botón Menú */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-slate-100 rounded-lg text-slate-700"
            data-testid="mobile-menu-btn"
          >
            <Menu size={24} />
          </button>
        </div>
      </header>

      {/* Mobile Sidebar - Sheet mejorado */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-[85vw] max-w-[320px] p-0 flex flex-col">
          {/* Header del Sidebar */}
          <SheetHeader className="p-4 border-b border-slate-200">
            <div className="flex items-center gap-3">
              {empresaActual?.logo_url ? (
                <div className="w-10 h-10 rounded-xl overflow-hidden bg-white border border-slate-200 flex-shrink-0">
                  <img 
                    src={empresaActual.logo_url} 
                    alt={empresaActual.nombre}
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-10 h-10 bg-blue-900 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">E</span>
                </div>
              )}
              <div className="min-w-0 flex-1">
                <SheetTitle 
                  className="text-lg font-bold truncate"
                  style={{ color: empresaActual?.color_principal || '#1e3a8a' }}
                >
                  {empresaActual?.nombre || "EDMUNDO"}
                </SheetTitle>
                <p className="text-xs text-slate-500">ERP + CRM</p>
              </div>
            </div>
          </SheetHeader>
          
          {/* Botón Inicio */}
          <div className="p-3 border-b border-slate-200">
            <button
              onClick={handleGoHome}
              className="w-full flex items-center gap-3 px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-900 rounded-xl font-medium transition-colors"
              style={{ 
                backgroundColor: empresaActual?.color_principal ? `${empresaActual.color_principal}15` : undefined,
                color: empresaActual?.color_principal || undefined
              }}
            >
              <Home size={20} />
              <span>Ir a Inicio</span>
            </button>
          </div>
          
          {/* Selector de Empresa */}
          {empresas.length > 1 && (
            <div className="p-3 border-b border-slate-200">
              <label className="text-xs font-medium text-slate-500 mb-2 block">EMPRESA</label>
              <select
                value={empresaActual?.id || ""}
                onChange={(e) => {
                  cambiarEmpresa(e.target.value);
                  setSidebarOpen(false);
                }}
                className="w-full p-3 bg-slate-50 rounded-xl border border-slate-200 text-slate-700 font-medium text-sm"
              >
                {empresas.map((empresa) => (
                  <option key={empresa.id} value={empresa.id}>
                    {empresa.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Navegación */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            <p className="text-xs font-medium text-slate-400 px-3 mb-2">MÓDULOS</p>
            {filteredNavItems.map((item) => renderNavItem(item, true, true))}
          </nav>

          {/* Usuario y Logout - Siempre visible al fondo */}
          <div className="border-t border-slate-200 bg-slate-50 p-4 safe-area-bottom">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <User size={20} className="text-blue-900" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-800 truncate text-sm">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={() => {
                setSidebarOpen(false);
                logout();
              }}
              className="w-full flex items-center justify-center gap-2 p-3 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl font-medium transition-colors"
              data-testid="mobile-logout-btn"
            >
              <LogOut size={18} />
              <span>Cerrar Sesión</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Mobile Bottom Navigation - Mejorada */}
      <nav className="mobile-nav lg:hidden safe-area-bottom">
        {mobileNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.path && location.pathname === item.path;
          
          // Si es link externo
          if (item.external) {
            return (
              <a
                key={item.external}
                href={item.external}
                target="_blank"
                rel="noopener noreferrer"
                className="mobile-nav-item"
                data-testid={`mobile-nav-external-${item.labelKey}`}
              >
                <Icon size={22} />
                <span className="text-[10px] font-medium mt-0.5">{item.shortLabel || item.label}</span>
              </a>
            );
          }
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={(e) => {
                if (location.pathname !== item.path) {
                  e.preventDefault();
                  startTransition(() => navigate(item.path));
                }
              }}
              className={`mobile-nav-item ${isActive ? "mobile-nav-item-active" : ""}`}
              data-testid={`mobile-nav-${(item.path || "").replace("/", "")}`}
            >
              <Icon size={22} />
              <span className="text-[10px] font-medium mt-0.5">{item.shortLabel || item.label}</span>
            </Link>
          );
        })}
        
        {/* Botón "Más" para acceder a otros módulos */}
        <Sheet open={moreMenuOpen} onOpenChange={setMoreMenuOpen}>
          <SheetTrigger asChild>
            <button 
              className={`mobile-nav-item ${moreMenuOpen ? "mobile-nav-item-active" : ""}`}
              data-testid="mobile-nav-more"
            >
              <MoreHorizontal size={22} />
              <span className="text-[10px] font-medium mt-0.5">Más</span>
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl max-h-[70vh]">
            <SheetHeader className="pb-4">
              <SheetTitle className="text-center">Más opciones</SheetTitle>
            </SheetHeader>
            <div className="grid grid-cols-3 gap-3 pb-6 overflow-y-auto">
              {/* Botón Inicio */}
              <button
                onClick={handleGoHome}
                className="flex flex-col items-center gap-2 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
              >
                <Home size={24} className="text-blue-600" />
                <span className="text-xs font-medium text-blue-900">Inicio</span>
              </button>
              
              {/* Items restantes */}
              {moreNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = item.path && location.pathname === item.path;
                const showBadge = item.showBadge && solicitudesPendientes > 0;
                
                // Si es link externo
                if (item.external) {
                  return (
                    <a
                      key={item.external}
                      href={item.external}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMoreMenuOpen(false)}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl transition-colors relative bg-slate-50 hover:bg-slate-100 text-slate-700"
                      data-testid={`more-nav-external-${item.labelKey}`}
                    >
                      <Icon size={24} />
                      <span className="text-xs font-medium text-center">{item.shortLabel || item.label}</span>
                    </a>
                  );
                }
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={(e) => {
                      setMoreMenuOpen(false);
                      if (location.pathname !== item.path) {
                        e.preventDefault();
                        startTransition(() => navigate(item.path));
                      }
                    }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-colors relative ${
                      isActive ? "bg-blue-100 text-blue-900" : "bg-slate-50 hover:bg-slate-100 text-slate-700"
                    }`}
                    data-testid={`more-nav-${(item.path || "").replace("/", "")}`}
                  >
                    <div className="relative">
                      <Icon size={24} />
                      {showBadge && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                          {solicitudesPendientes > 9 ? "9+" : solicitudesPendientes}
                        </span>
                      )}
                    </div>
                    <span className="text-xs font-medium text-center">{item.shortLabel || item.label}</span>
                  </Link>
                );
              })}
              
              {/* Cerrar Sesión */}
              <button
                onClick={() => {
                  setMoreMenuOpen(false);
                  logout();
                }}
                className="flex flex-col items-center gap-2 p-4 bg-red-50 hover:bg-red-100 rounded-xl transition-colors"
                data-testid="more-logout-btn"
              >
                <LogOut size={24} className="text-red-600" />
                <span className="text-xs font-medium text-red-700">Salir</span>
              </button>
            </div>
          </SheetContent>
        </Sheet>
      </nav>

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0">
        <div className="p-3 sm:p-4 md:p-6 lg:p-8 max-w-7xl mx-auto animate-fade-in">
          {children}
        </div>
      </main>
    </div>
  );
}
